import { BRAND_TYPE } from '@community_dev/types/lib/api/v2/TCR'
import { useQueryClient } from '@tanstack/react-query'
import { Typography } from 'antd'
import { useState, type JSX } from 'react'

import { useClientSettings } from '../../../hooks/useClientSettings'
import { SettingsRow } from '../../../Settings'

import {
  isValidDailySegmentLimitInput,
  toDailySegmentLimitDisplay,
  toDailySegmentLimitInput,
  toDailySegmentLimitPatch,
} from './dailySegmentLimit'

import { patchClientSettings } from 'api/clients'
import { CAPABILITIES } from 'constants/capabilities'
import { QUERIES } from 'constants/queries'
import { useHasCapability } from 'hooks/useCapabilities'
import { useTCRRegistration } from 'hooks/useTCR'
import { InlineEditField } from 'shared/components/InlineEditField/InlineEditField'
import { parseAuthToken } from 'utils/api'

export function DailySegmentLimitRow({ client }: { client: { id: string } }): JSX.Element | null {
  const queryClient = useQueryClient()
  const canEdit = useHasCapability(CAPABILITIES.ADMIN.BILLING.WRITE)
  const [token] = useState(() => parseAuthToken())
  const { settings, isLoading } = useClientSettings(client.id)
  const {
    data: registration,
    isLoading: isTcrLoading,
    isError: isTcrError,
  } = useTCRRegistration({
    clientId: client.id,
    seatId: token?.sub as string,
  })
  const isSoleProp = registration?.data?.brandType === BRAND_TYPE.STARTER
  const isReadOnly = isSoleProp || isTcrError
  const displayValue = toDailySegmentLimitDisplay(settings?.dailyMessageSegmentLimit)
  const readOnlyReason = isSoleProp ? 'Sole Prop — not editable' : 'brand type unavailable — not editable'

  if (isLoading || isTcrLoading) return null

  return (
    <SettingsRow description="Daily message segments (0 disables the limit)" label="Daily Segment Limit">
      {isReadOnly ? (
        <Typography.Text>{`${displayValue || '—'} (${readOnlyReason})`}</Typography.Text>
      ) : (
        <InlineEditField
          defaultValue={displayValue}
          disabled={!canEdit}
          name="Daily Segment Limit"
          normalize={toDailySegmentLimitInput}
          onSuccess={() => queryClient.invalidateQueries([QUERIES.CLIENT_SETTINGS, { id: client.id }])}
          placeholder="0"
          prepare={(value) => ({
            id: client.id,
            body: { daily_message_segment_limit: toDailySegmentLimitPatch(value) },
          })}
          updateEntity={patchClientSettings}
          validation={isValidDailySegmentLimitInput}
        >
          {({ valid }) =>
            valid ? null : <Typography.Text type="danger">Enter a whole number of 0 or more.</Typography.Text>
          }
        </InlineEditField>
      )}
    </SettingsRow>
  )
}
